import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { store } from './store';
import { selectIsAuthenticated } from './store/slices/authSlice';
import { selectIsOpen, selectIsMobile, closeSidebar, setMobileMode } from './store/slices/sidebarSlice';
import SidePanel from './components/SidePanel';
import TopBar from './components/TopBar';
import MobileMenuButton from './components/MobileMenuButton';
import AppRoutes from './routes/AppRoutes';
import 'react-toastify/dist/ReactToastify.css';

const AppLayout = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isOpen = useSelector(selectIsOpen);
  const isMobile = useSelector(selectIsMobile);

  // Handle mobile responsiveness.
  //
  // matchMedia fires only when the breakpoint is actually crossed, where a
  // resize listener fired on every pixel of a drag and dispatched an action
  // each time.
  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const apply = (matches) => dispatch(setMobileMode(matches));

    apply(query.matches); // Initial check
    const onChange = (event) => apply(event.matches);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [dispatch]);

  const getMainContentMargin = () => {
    if (!isAuthenticated) return 'ml-0';
    if (isMobile && !isOpen) return 'ml-0';
    if (!isOpen) return 'ml-16';
    return 'ml-64';
  };

  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-background text-foreground transition-colors duration-300">
        {/* Top Bar - only show when authenticated */}
        {isAuthenticated && <TopBar />}
        
        <div className="flex flex-1">
          {/* Side Panel - only show when authenticated */}
          <SidePanel />
          
          {/* Mobile Menu Button */}
          <MobileMenuButton />
          
          {/* Main Content Area
              min-w-0 is load-bearing: a flex item defaults to min-width:auto,
              so without it a wide table refuses to shrink and widens the whole
              document instead of scrolling inside its own container — which
              drags the top bar out of view sideways. */}
          <div className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${getMainContentMargin()}`}>
            <div className="min-h-full bg-background">
              <AppRoutes />
            </div>
          </div>

          {/* Mobile Overlay */}
          {isMobile && isOpen && isAuthenticated && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => dispatch(closeSidebar())}
            />
          )}
        </div>
      </div>
      
      {/* Toast Notifications Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className={isAuthenticated ? "mt-12" : ""}
      />
    </Router>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <AppLayout />
    </Provider>
  );
};

export default App;
